import React, { FC, useEffect } from 'react';

import { Typography } from '@mui/material';
import { Box } from '@mui/system';

import {
  chatActions,
  chatActiveTab,
  ClientChatTypes,
  ConfirmationTypes,
  resetStatus,
  successOrSelector,
} from '@pharmaplan/common';

import { resetDialog, setDialog } from '../../../../reducers/dialogReducer';
import ConfirmationModal from '../../../Modals/ConfirmationModal';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import genericClasses from '../../../../theme/GenericClasses';
import ChatManager from '../../../../hooks/Chat/ChatManager';
import useHideChat from '../../../../hooks/Chat/useHideChat';
import { Constants } from '../../../../helpers/Constants';
import ChatAvatar from '../../../common/Chat/ChatAvatar';
import CustomButton from '../../../common/CustomButton';
import strings from '../../../../localization';

import styles from './styles';

interface IGroupChannelTitle {
  title: string;
  url: string;
  isViewingEngagedChannel?: boolean;
}

const { makeChannelAvailable: makeChannelAvailableConfirmation } = ConfirmationTypes;
const { pharmacyHidesChat, professionalHidesChat, adminCloseChat } = chatActions;
const { pharmacist, pharmacy } = ClientChatTypes;

const GroupChannelTitle: FC<IGroupChannelTitle> = ({
  title,
  url,
  isViewingEngagedChannel,
}) => {
  const dispatch = useAppDispatch();

  const { makeChannelAvailable } = ChatManager();
  let { options } = useHideChat(url);

  const activeTab = useAppSelector(chatActiveTab);
  const success = useAppSelector((state) =>
    successOrSelector(
      [adminCloseChat, professionalHidesChat, pharmacyHidesChat],
      state,
    ));

  const isPharmacyOrPharmacist = activeTab === pharmacist || activeTab === pharmacy;

  const releaseChat = () => {
    makeChannelAvailable(url);
    dispatch(resetDialog());
  };

  if (isViewingEngagedChannel) {
    options = {
      action: releaseChat,
      label: strings.releaseChat,
      type: makeChannelAvailableConfirmation,
    };
  }

  const { action, label, type } = options;

  const showModal = (customAction?: () => void) =>
    () => {
      dispatch(
        setDialog({
          Component: (
            <ConfirmationModal
              type={type}
              confirmAction={customAction ?? action}
              customMessage={
              isPharmacyOrPharmacist ? (
                <Typography sx={styles.message}>
                  {strings.hideChatConfirmation}
                </Typography>
              ) : null
            }
            />
          ),
          showCloseButton: false,
          heading: {
            title: '',
          },
        }),
      );
    };

  useEffect(() => {
    if (success) {
      dispatch(resetStatus([chatActions.adminCloseChat]));
      dispatch(resetDialog());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  return (
    <Box sx={styles.container}>
      <Box sx={genericClasses.center}>
        {isPharmacyOrPharmacist && <ChatAvatar url={url} />}
        <Typography sx={styles.title}>{title}</Typography>
      </Box>
      <Box sx={styles.buttonContainer}>
        <CustomButton
          variant={Constants.variant.outlined}
          label={label}
          onClick={showModal()}
        />
      </Box>
    </Box>
  );
};

export default GroupChannelTitle;
